/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:max-line-length

import { Type } from '@angular/core';
import { Data, Route } from '@angular/router';
import { AuthGuard, AuthRouteConfig, CanLeave, LeaveGuard } from '@gh/common-shared';
import { DialogRouteConfig } from '@gh/core-ui';
import { Observable } from 'rxjs';
import { ContextableDialogComponent, ContextablePageComponent, ContextRoute, ContextRouteConfig } from './context';
import { InitContextViewService } from './init-context-service';
import {
  ContextableInitDialogViewResolver,
  ContextableInitViewResolver,
  InitContextViewResolver,
} from './init-context-view-resolver.service';
import { LeaveContextGuard } from './leave-context-guard.service';

export interface BaseContextRoute {
  path: string;
  component: Type<any>;
  static?: Data;
}

/**
 * This interface defines configuration for route having optional {@link CanLeave} guard.
 */
export interface LeaveContextRoute {
  leave?: boolean;
}

/**
 * This interface defines configuration for route having mandatory {@link CanLeave} guard.
 */
export interface CanLeaveContextRoute<C extends CanLeave> {
  leave: true;
  component: Type<C>;
}

/**
 * This interface defines configuration for route which does not have {@link CanLeave} guard.
 */
export interface CannotLeaveContextRoute {
  leave?: false;
}

/**
 * This interface defines configuration for view initialization service.
 * Developer should provide
 * either service implementing {@link InitContextViewService} interface
 * or any service along with selector which retrieves data from the provided service
 */
export interface InitContextViewRoute<I> {
  init?: Type<InitContextViewService> | {
    service: Type<I>;
    selector: string | ((service: I, route?: ContextRoute) =>
      { [name: string]: Observable<any> } | any);
    noWaiting?: boolean;
  };
}

/**
 * Configuration for route based on context route
 */
export interface BaseContextableRouteConfig {
  path?: string;
  pathMatch?: string;
  contextRoute: string | ContextRouteConfig;
}

/**
 * Returns init data from the given context route
 */
export function getContextRouteInitData(route: ContextRoute): any {
  return route.data['init'];
}

/**
 * Returns static data associated with the given context route
 */
export function getContextRouteStaticData(route: ContextRoute): any {
  return route.data['static'];
}

/**
 * Returns current value of the specified parameter for the given contxt route.
 */
export function getContextRouteParam(route: ContextRoute, name: string): any {
  return route.params[name];
}

/**
 * This method creates context route and guarantees that route configuration is correct.
 *
 * All routes can have
 * - leave guard ({@link LeaveGuard}) that allows component to control if user can leave the page.
 * - init data loader that allows to load initial data before view has opened
 */
export function createContextRoute<C extends CanLeave, I>(route: BaseContextRoute & CanLeaveContextRoute<C> & InitContextViewRoute<I>): ContextRouteConfig;
export function createContextRoute<V>(route: BaseContextRoute & CannotLeaveContextRoute & InitContextViewRoute<V>): ContextRouteConfig;
export function createContextRoute<V>(route: BaseContextRoute & LeaveContextRoute & InitContextViewRoute<V>): ContextRouteConfig {
  return {
    path: route.path,
    component: route.component,
    canDeactivate: route.leave ? LeaveContextGuard : void 0,
    resolve: { init: InitContextViewResolver },
    data: {
      initView: route.init,
      static: route.static,
    },
  };
}

/**
 * This method creates dialog route based on provided context route configuration
 * and guarantees that route configuration is correct.
 *
 * Dialog opened by this configuration is resized to the whole browser window.
 */
// tslint:disable-next-line:max-line-length
export function createContextableDialogRoute<I>(contextRoute: ContextRouteConfig): DialogRouteConfig<ContextableDialogComponent<I>> {
  return {
    component: ContextableDialogComponent,
    resolve: ContextableInitDialogViewResolver,
    data: { contextRoute },
    config: {
      disableClose: true,
      width: '100vw', height: '100vh',
      maxWidth: '100vw', maxHeight: '100vh',
    },
  };
}

/**
 * This method creates route based on context route and guarantees that route configuration is correct.
 *
 * Contextable routes takes the most part of its configuration from corresponding context route.
 * This methods provides additional configuration
 * - authentication guard ({@link AuthGuard}) that verifies user permissions before route is opened.
 */
export function createContextableRoute(route: BaseContextableRouteConfig & AuthRouteConfig): Route {
  return {
    path: route.path,
    pathMatch: route.pathMatch,
    component: ContextablePageComponent,
    canActivate: route.auth ? [AuthGuard] : [],
    canDeactivate: [LeaveGuard],
    resolve: { context: ContextableInitViewResolver },
    data: {
      applicationView: route.auth ? route.auth.applicationView : [],
      permission: route.auth ? route.auth.permission : [],
      contextRoute: route.contextRoute,
    },
  };
}
